import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, ChevronRight, Book, Code, Settings, Shield,
  Zap, FileText, ExternalLink, Search
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { MarketingLayout } from '../../components/marketing/MarketingLayout';

const docCategories = [
  {
    title: 'Getting Started',
    icon: Zap,
    description: 'Quick start guides and onboarding documentation',
    links: [
      { title: 'Platform Overview', href: '#' },
      { title: 'First Connector Setup', href: '#' },
      { title: 'Creating Your First Case', href: '#' },
      { title: 'Understanding Action Bundles', href: '#' }
    ]
  },
  {
    title: 'Connectors',
    icon: Settings,
    description: 'Integration guides for all supported systems',
    links: [
      { title: 'Okta Integration', href: '#' },
      { title: 'Azure AD / Entra ID', href: '#' },
      { title: 'BambooHR', href: '#' },
      { title: 'Slack & Teams', href: '#' }
    ]
  },
  {
    title: 'Policy Studio',
    icon: Shield,
    description: 'Configure approval rules and automation policies',
    links: [
      { title: 'Policy Language Reference', href: '#' },
      { title: 'Approval Routing', href: '#' },
      { title: 'Autonomy Levels', href: '#' },
      { title: 'Policy Templates', href: '#' }
    ]
  },
  {
    title: 'API Reference',
    icon: Code,
    description: 'REST API documentation and SDKs',
    links: [
      { title: 'Authentication', href: '#' },
      { title: 'Cases API', href: '#' },
      { title: 'Webhooks', href: '#' },
      { title: 'Connector SDK', href: '#' }
    ]
  }
];

const popularArticles = [
  { title: 'Configuring SSO with Okta', category: 'Connectors', readTime: '5 min' },
  { title: 'Creating Custom Approval Workflows', category: 'Policy Studio', readTime: '8 min' },
  { title: 'Onboarding Action Bundle Template', category: 'Templates', readTime: '10 min' },
  { title: 'Evidence Pack Structure', category: 'Compliance', readTime: '6 min' },
  { title: 'Webhook Event Reference', category: 'API', readTime: '4 min' },
  { title: 'Autonomy Ladder Best Practices', category: 'Operations', readTime: '7 min' }
];

const resources = [
  {
    title: 'API Reference',
    description: 'Complete REST API documentation with examples',
    icon: Code,
    href: '#'
  },
  {
    title: 'Changelog',
    description: 'Recent updates and new features',
    icon: FileText,
    href: '#'
  },
  {
    title: 'Status Page',
    description: 'Platform status and incident history',
    icon: Shield,
    href: '#'
  }
];

export const DocsLanding = () => {
  return (
    <MarketingLayout>
      <div className="min-h-screen">
        {/* Hero */}
        <section className="py-16 px-6 bg-white border-b border-border">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
              <Link to="/" className="hover:text-foreground">Home</Link>
              <ChevronRight className="w-4 h-4" />
              <span>Documentation</span>
            </div>
            <div className="flex items-start gap-6">
              <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                <Book className="w-7 h-7 text-primary" />
              </div>
              <div className="flex-1">
                <h1 className="text-3xl md:text-4xl font-semibold text-foreground mb-4 tracking-tight">
                  Documentation
                </h1>
                <p className="text-lg text-muted-foreground max-w-3xl leading-relaxed mb-6">
                  Guides, API references, and resources to help you get the most out of Vedaya.
                </p>
                <div className="relative max-w-xl">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="Search documentation..."
                    className="w-full pl-10 pr-4 py-3 bg-secondary/30 border border-border rounded-lg text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Categories */}
        <section className="py-16 px-6 bg-secondary/30">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl font-semibold text-foreground mb-8">Browse by Category</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {docCategories.map((cat, idx) => (
                <div key={idx} className="bg-white border border-border rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <cat.icon className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-foreground">{cat.title}</h3>
                      <p className="text-sm text-muted-foreground">{cat.description}</p>
                    </div>
                  </div>
                  <ul className="space-y-2">
                    {cat.links.map((link, lidx) => (
                      <li key={lidx}>
                        <a
                          href={link.href}
                          className="flex items-center gap-2 text-sm text-foreground hover:text-primary transition-colors"
                        >
                          <ChevronRight className="w-4 h-4 text-muted-foreground" />
                          {link.title}
                        </a>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-4 pt-4 border-t border-border">
                    <a href="#" className="text-sm text-primary hover:underline flex items-center gap-1">
                      View all {cat.title.toLowerCase()} docs
                      <ArrowRight className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Popular Articles */}
        <section className="py-16 px-6 bg-white border-t border-border">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl font-semibold text-foreground mb-8">Popular Articles</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {popularArticles.map((article, idx) => (
                <a
                  key={idx}
                  href="#"
                  className="bg-secondary/30 border border-border rounded-lg p-4 hover:border-primary/50 transition-colors"
                >
                  <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
                    <span className="px-2 py-0.5 bg-secondary rounded">{article.category}</span>
                    <span>{article.readTime} read</span>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground">{article.title}</h3>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* Resources */}
        <section className="py-16 px-6 bg-secondary/30 border-t border-border">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl font-semibold text-foreground mb-8">Developer Resources</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {resources.map((res, idx) => (
                <a
                  key={idx}
                  href={res.href}
                  className="bg-white border border-border rounded-lg p-5 hover:border-primary/50 transition-colors"
                >
                  <res.icon className="w-6 h-6 text-primary mb-3" />
                  <h3 className="text-base font-semibold text-foreground mb-1">{res.title}</h3>
                  <p className="text-sm text-muted-foreground">{res.description}</p>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* Support CTA */}
        <section className="py-16 px-6 bg-white border-t border-border">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl font-semibold text-foreground mb-4">Need Help?</h2>
            <p className="text-muted-foreground mb-8">
              Can't find what you're looking for? Our support team is here to help.
            </p>
            <div className="flex justify-center gap-3">
              <Button asChild size="lg" variant="outline">
                <a href="mailto:support@vedaya.ai">
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Contact Support
                </a>
              </Button>
              <Button asChild size="lg" variant="outline">
                <Link to="/demo/request">Schedule a Call</Link>
              </Button>
            </div>
          </div>
        </section>
      </div>
    </MarketingLayout>
  );
};

export default DocsLanding;
